import { FILE_TYPES_BY_KEY } from "./FileTypes";

export const DIMENSIONAL_METADATA_KEYS = {
  x: "ideas.spacingInfo.numPixels.x" as const,
  y: "ideas.spacingInfo.numPixels.y" as const,
  z: "ideas.spacingInfo.numPixels.z" as const,
};

export const SERIES_TIMING_KEYS = {
  num: "ideas.timingInfo.start.secsSinceEpoch.num",
  dem: "ideas.timingInfo.start.secsSinceEpoch.den",
};

export const metadataMap: {
  [value in keyof typeof FILE_TYPES_BY_KEY]: Record<string, string> | undefined;
} = {
  cell_set: {
    "isx.extraProperties.idps.cellset.method": "Cell extraction method",
    "ideas.metrics.num_accepted_cells": "Number of accepted cells",
    "ideas.metrics.num_rejected_cells": "Number of rejected cells",
    "ideas.metrics.num_undecided_cells": "Number of undecided cells",
    "ideas.metrics.total_num_cells": "Total number of cells",
    "ideas.timingInfo.numTimes": "Number of frames",
    "ideas.timingInfo.blank": "Blank frames",
    "ideas.timingInfo.cropped": "Cropped frames",
    "ideas.timingInfo.dropped": "Dropped frames",
    "ideas.timingInfo.num_blank": "Number of blank frames",
    "ideas.timingInfo.num_cropped": "Number of cropped frames",
    "ideas.timingInfo.num_dropped": "Number of dropped frames",
    "ideas.timingInfo.sampling_rate": "Sampling rate (Hz)",
    "ideas.timingInfo.start_time": "Start time",
    "ideas.timingInfo.duration": "Duration (s)",
    "ideas.timingInfo.end_time": "End time",
    "isx.extraProperties.microscope.fps.fps": "Acquisition sampling rate (Hz)",
    "isx.extraProperties.processingInterface.recordingUUID":
      "Acquisition recording ID",
    "ideas.microscope.focus": "E-focus",
    "isx.extraProperties.idps.channel": "Channel",
    "isx.extraProperties.microscope.widefield": "Widefield",
    "ideas.Cell Extraction Method": "Cell extraction method",
    "ideas.Number of Frames": "Number of frames",
    "ideas.Sampling Rate (Hz)": "Sampling rate (Hz)",
    "ideas.Recording Duration (s)": "Recording duration (s)",
    "ideas.Number of Extracted ROIs": "Number of extracted ROIs",
    "ideas.Number of Accepted ROIs": "Number of accepted ROIs",
    "ideas.Number of Rejected ROIs": "Number of rejected ROIs",
    "ideas.Average trace amplitude": "Average trace amplitude",
    "ideas.Multisession registration method":
      "Multisession registration method",
    "ideas.Session index among registered sessions":
      "Session index among registered sessions",
  } as const,
  movie: {
    "isx.extraProperties.microscope.type": "Miniscope",
    "isx.extraProperties.microscope.fps.fps": "Acquisition Frame Rate (Hz)",
    "isx.cameraName": "Camera name",
    "ideas.timingInfo.numTimes": "Number of frames",
    "ideas.timingInfo.blank": "Number of blank frames",
    "ideas.timingInfo.cropped": "Number of cropped frames",
    "ideas.timingInfo.dropped": "Number of dropped frames",
    "ideas.timingInfo.sampling_rate": "Sampling Rate (Hz)",
    "nvision.droppedFrames": "Dropped frames",
    [DIMENSIONAL_METADATA_KEYS.x]: "Width (pixels)",
    [DIMENSIONAL_METADATA_KEYS.y]: "Height (pixels)",
    "ideas.microscope.focus": "E-focus",
    "isx.extraProperties.probe.name": "Probe",
    "isx.extraProperties.microscope.serial": "Miniscope Serial Number",
    "isx.extraProperties.processingInterface.recordingUUID":
      "Acquisition Recording ID",
    "dlc.model_neural_net_type": "Model neural network type",
    "dlc.body_parts": "Body parts",
    "ideas.dlc.task": "Model task name",
    "ideas.dlc.scorer": "Model scorer",
    "ideas.dlc.date": "Model training date",
    "ideas.dlc.multi_animal_project": "Multi-animal project",
    "ideas.dlc.model_neural_net_type": "Model neural network type",
    "ideas.dlc.body_parts": "Body parts",
    "ideas.dlc.snapshot": "Model snapshot number",
    "isx.extraProperties.idps.channel": "Channel",
  } as const,
  miniscope_movie: {
    "isx.extraProperties.microscope.type": "Miniscope",
    "isx.extraProperties.microscope.fps.fps": "Acquisition Frame Rate (Hz)",
    "isx.cameraName": "Camera name",
    "ideas.timingInfo.numTimes": "Number of frames",
    "ideas.timingInfo.blank": "Blank frames",
    "ideas.timingInfo.cropped": "Cropped frames",
    "ideas.timingInfo.dropped": "Dropped frames",
    "ideas.timingInfo.num_blank": "Number of blank frames",
    "ideas.timingInfo.num_cropped": "Number of cropped frames",
    "ideas.timingInfo.num_dropped": "Number of dropped frames",
    "ideas.timingInfo.sampling_rate": "Sampling rate (Hz)",
    "ideas.timingInfo.start_time": "Start time",
    "ideas.timingInfo.duration": "Duration (s)",
    "ideas.timingInfo.end_time": "End time",
    "nvision.droppedFrames": "Dropped frames",
    [DIMENSIONAL_METADATA_KEYS.x]: "Width (pixels)",
    [DIMENSIONAL_METADATA_KEYS.y]: "Height (pixels)",
    "ideas.microscope.focus": "E-focus",
    "isx.extraProperties.probe.name": "Probe",
    "isx.extraProperties.microscope.serial": "Miniscope Serial Number",
    "isx.extraProperties.processingInterface.recordingUUID":
      "Acquisition Recording ID",
    "dlc.model_neural_net_type": "Model neural network type",
    "dlc.body_parts": "Body parts",
    "isx.extraProperties.idps.channel": "Channel",
    "isx.extraProperties.microscope.widefield": "Widefield",
  } as const,
  nvision_movie: {
    "isx.extraProperties.microscope.type": "Miniscope",
    "isx.extraProperties.microscope.fps.fps": "Acquisition Frame Rate (Hz)",
    "isx.cameraName": "Camera name",
    "ideas.timingInfo.numTimes": "Number of frames",
    "ideas.timingInfo.dropped": "Dropped frames",
    "ideas.timingInfo.num_dropped": "Number of dropped frames",
    "ideas.timingInfo.sampling_rate": "Sampling rate (Hz)",
    "ideas.timingInfo.start_time": "Start time",
    "ideas.timingInfo.duration": "Duration (s)",
    "ideas.timingInfo.end_time": "End time",
    [DIMENSIONAL_METADATA_KEYS.x]: "Width (pixels)",
    [DIMENSIONAL_METADATA_KEYS.y]: "Height (pixels)",
    "ideas.microscope.focus": "E-focus",
    "isx.extraProperties.probe.name": "Probe",
    "isx.extraProperties.microscope.serial": "Miniscope Serial Number",
    "isx.extraProperties.processingInterface.recordingUUID":
      "Acquisition Recording ID",
    "dlc.model_neural_net_type": "Model neural network type",
    "dlc.body_parts": "Body parts",
    "isx.extraProperties.idps.channel": "Channel",
    "ideas.pixelsPerCm": "Calibration (px/cm)",
  } as const,
  event_aligned_data: {
    event_type: "Event type",
    "ideas.metrics.num_valid_events": "Number of events",
    "ideas.metrics.num_up_modulated_cells": "Number of up-modulated cells",
    "ideas.metrics.num_down_modulated_cells": "Number of down-modulated cells",
    "ideas.metrics.num_non_modulated_cells": "Number of non-modulated cells",
    "ideas.timingInfo.numTimes": "Number of timepoints",
    "ideas.timingInfo.sampling_rate": "Sampling Rate (Hz)",
    "isx.extraProperties.microscope.fps.fps": "Acquisition Sampling Rate (Hz)",
  } as const,
  event_aligned_neural_data: {
    event_type: "Event type",
    "ideas.metrics.num_valid_events": "Number of events",
    "ideas.metrics.num_up_modulated_cells": "Number of up-modulated cells",
    "ideas.metrics.num_down_modulated_cells": "Number of down-modulated cells",
    "ideas.metrics.num_non_modulated_cells": "Number of non-modulated cells",
    "ideas.timingInfo.numTimes": "Number of timepoints",
    "ideas.timingInfo.sampling_rate": "Sampling Rate (Hz)",
    "isx.extraProperties.microscope.fps.fps": "Acquisition Sampling Rate (Hz)",
  } as const,
  statistics: {
    event_type: "Event type",
    "ideas.metrics.num_valid_events": "Number of events",
    "ideas.metrics.num_up_modulated_cells": "Number of up-modulated cells",
    "ideas.metrics.num_down_modulated_cells": "Number of down-modulated cells",
    "ideas.metrics.num_non_modulated_cells": "Number of non-modulated cells",
    "ideas.timingInfo.numTimes": "Number of timepoints",
    "ideas.timingInfo.sampling_rate": "Sampling Rate (Hz)",
    "isx.extraProperties.microscope.fps.fps": "Acquisition Sampling Rate (Hz)",
  } as const,
  gpio_data: {
    "isx.extraProperties.microscope.type": "Miniscope",
    "isx.extraProperties.probe.name": "Probe",
    "isx.extraProperties.microscope.serial": "Miniscope serial number",
    "ideas.metrics.num_channels": "Number of channels",
    "isx.channel list": "Channel names",
    "ideas.timingInfo.numTimes": "Number of timepoints per channel",
    "ideas.timingInfo.sampling_rate": "Sampling rate (Hz)",
    "ideas.timingInfo.start_time": "Start time",
    "ideas.timingInfo.duration": "Duration (s)",
    "ideas.timingInfo.end_time": "End time",
  } as const,
  imu_data: {
    "isx.extraProperties.microscope.type": "Miniscope",
    "isx.extraProperties.probe.name": "Probe",
    "isx.extraProperties.microscope.serial": "Miniscope serial number",
    "ideas.metrics.num_channels": "Number of channels",
    "isx.channel list": "Channel names",
    "ideas.timingInfo.numTimes": "Number of timepoints per channel",
    "isx.extraProperties.microscope.fps.fps": "Acquisition Sampling Rate (Hz)",
    "ideas.timingInfo.sampling_rate": "Sampling rate (Hz)",
    "ideas.timingInfo.start_time": "Start time",
    "ideas.timingInfo.duration": "Duration (s)",
    "ideas.timingInfo.end_time": "End time",
  } as const,
  timestamp_events: {
    "ideas.metrics.total_num_events": "Number of events",
    "ideas.metrics.num_event_types": "Number of event types",
    "ideas.metrics.event_types": "Event types",
    "ideas.metrics.num_events_per_type": "Number of events per event type",
  } as const,
  neural_events: {
    "ideas.timingInfo.numFrames": "Number of frames",
    "ideas.timingInfo.sampling_rate": "Sampling rate (Hz)",
    "ideas.timingInfo.start_time": "Start time",
    "ideas.timingInfo.duration": "Duration (s)",
    "ideas.timingInfo.end_time": "End time",
    "ideas.metrics.total_num_cells": "Number of cells",
    "ideas.timingInfo.numSamples": "Number of events per cell",
    "isx.channel list": "Cell names",
    "ideas.microscope.focus": "E-focus",
    "isx.extraProperties.idps.channel": "Channel",
    "isx.extraProperties.microscope.widefield": "Widefield",
    "ideas.Cell Extraction Method": "Cell extraction method",
    "ideas.Number of Frames": "Number of frames",
    "ideas.Sampling Rate (Hz)": "Sampling rate (Hz)",
    "ideas.Recording Duration (s)": "Recording duration (s)",
    "ideas.Average event rate (Hz)": "Average event rate (Hz)",
    "ideas.Minimum event rate (Hz)": "Minimum event rate (Hz)",
    "ideas.Maximum event rate (Hz)": "Maximum event rate (Hz)",
    "ideas.Multisession registration method":
      "Multisession registration method",
    "ideas.Session index among registered sessions":
      "Session index among registered sessions",
  } as const,
  image: {
    [DIMENSIONAL_METADATA_KEYS.x]: "Width (pixels)",
    [DIMENSIONAL_METADATA_KEYS.y]: "Height (pixels)",
    [DIMENSIONAL_METADATA_KEYS.z]: "Number of images", // for single-image and multi-image tiff files
  } as const,
  miniscope_image: {
    [DIMENSIONAL_METADATA_KEYS.x]: "Width (pixels)",
    [DIMENSIONAL_METADATA_KEYS.y]: "Height (pixels)",
  } as const,
  vessel_set: {
    "isx.extraProperties.idps.vesselset.type": "Vessel Set Type",
    "ideas.metrics.num_accepted_vessels": "Number of accepted vessels",
    "ideas.metrics.num_rejected_vessels": "Number of rejected vessels",
    "ideas.metrics.num_undecided_vessels": "Number of undecided vessels",
    "ideas.metrics.total_num_vessels": "Total number of vessels",
    "ideas.timingInfo.numTimes": "Number of frames",
    "ideas.timingInfo.blank": "Blank frames",
    "ideas.timingInfo.cropped": "Cropped frames",
    "ideas.timingInfo.dropped": "Dropped frames",
    "ideas.timingInfo.num_blank": "Number of blank frames",
    "ideas.timingInfo.num_cropped": "Number of cropped frames",
    "ideas.timingInfo.num_dropped": "Number of dropped frames",
    "ideas.timingInfo.sampling_rate": "Sampling rate (Hz)",
    "ideas.timingInfo.start_time": "Start time",
    "ideas.timingInfo.duration": "Duration (s)",
    "ideas.timingInfo.end_time": "End time",
    "isx.extraProperties.processingInterface.recordingUUID":
      "Acquisition Recording ID",
    "ideas.microscope.focus": "E-focus",
    "isx.extraProperties.idps.channel": "Channel",
    "isx.extraProperties.microscope.widefield": "Wide field",
    "isx.extraProperties.idps.vesselset.estimationMethod":
      "Vessel Diameter Estimation Method",
    "isx.extraProperties.idps.vesselset.units": "Trace Units",
    "isx.extraProperties.idps.vesselset.timeWindow": "Time Window",
    "isx.extraProperties.idps.vesselset.timeIncrement": "Time Increment",
  },
  bundle: {},
  report: {},
  behavior_annotations: {
    "ideas.metrics.num_rows": "Number of rows",
    "ideas.metrics.num_columns": "Number of columns",
    "ideas.dataset.states": "States",
  },
  combined_peri_event_traces: {
    "ideas.metrics.num_up_modulated_cells": "Number of up-modulated cells",
    "ideas.metrics.num_down_modulated_cells": "Number of down-modulated cells",
    "ideas.metrics.num_non_modulated_cells": "Number of non-modulated cells",
    "ideas.timingInfo.numTimes": "Number of timepoints",
    "ideas.timingInfo.sampling_rate": "Sampling Rate (Hz)",
  },
  combined_peri_event_statistics: {
    "ideas.metrics.num_up_modulated_cells": "Number of up-modulated cells",
    "ideas.metrics.num_down_modulated_cells": "Number of down-modulated cells",
    "ideas.metrics.num_non_modulated_cells": "Number of non-modulated cells",
    "ideas.timingInfo.numTimes": "Number of timepoints",
    "ideas.timingInfo.sampling_rate": "Sampling Rate (Hz)",
  },
  peri_event_comparison_data: {
    "ideas.timingInfo.numTimes": "Number of timepoints",
    "ideas.timingInfo.sampling_rate": "Sampling Rate (Hz)",
  },
  combined_population_activity_data: {
    "ideas.dataset.states": "States",
    "ideas.metrics.total_num_cells": "Number of cells",
  },
  population_activity_comparison_data: {
    "ideas.dataset.states": "States",
    "ideas.metrics.total_num_cells_group1": "Number of cells (first group)",
    "ideas.metrics.total_num_cells_group2": "Number of cells (second group)",
  },
  correlation_comparison_data: {
    "ideas.dataset.states": "States",
    "ideas.metrics.total_num_cells_group1": "Number of cells (first group)",
    "ideas.metrics.total_num_cells_group2": "Number of cells (second group)",
  },
  calcium_image_qc_report: {},
  calcium_image_qc_data: {},
  dlc_model_predictions_data: {
    "ideas.dlc.task": "Model task name",
    "ideas.dlc.scorer": "Model scorer",
    "ideas.dlc.date": "Model training date",
    "ideas.dlc.multi_animal_project": "Multi-animal project",
    "ideas.dlc.model_neural_net_type": "Model neural network type",
    "ideas.dlc.body_parts": "Body parts",
    "ideas.dlc.snapshot": "Model snapshot number",
  },
  nwb_data: {
    "ideas.dataset.session_start_time": "Session start time",
    "ideas.dataset.dandi_compliant": "DANDI compliant",
  },
  experiment_annotations: {
    "ideas.metrics.num_rows": "Number of rows",
    "ideas.metrics.num_columns": "Number of columns",
    "ideas.dataset.states": "States",
    "ideas.recording_uuid": "Recording UUID",
    "ideas.total_distance_traveled_px": "Total distance traveled (px)",
    "ideas.min_speed_px_s": "Minimum speed (px/s)",
    "ideas.max_speed_px_s": "Maximum speed (px/s)",
    "ideas.mean_speed_px_s": "Mean speed (px/s)",
    "ideas.median_speed_px_s": "Median speed (px/s)",
    "ideas.std_speed_px_s": "Standard deviation speed (px/s)",
    "ideas.total_distance_traveled_cm": "Total distance traveled (cm)",
    "ideas.min_speed_cm_s": "Minimum speed (cm/s)",
    "ideas.max_speed_cm_s": "Maximum speed (cm/s)",
    "ideas.mean_speed_cm_s": "Mean speed (cm/s)",
    "ideas.median_speed_cm_s": "Median speed (cm/s)",
    "ideas.std_speed_cm_s": "Standard deviation speed (cm/s)",
    "ideas.time_spent_resting": "Time spent resting (s)",
    "ideas.time_spent_moving": "Time spent moving (s)",
    "ideas.dlc.task": "Model task name",
    "ideas.dlc.scorer": "Model scorer",
    "ideas.dlc.date": "Model training date",
    "ideas.dlc.multi_animal_project": "Multi-animal project",
    "ideas.dlc.model_neural_net_type": "Model neural network type",
    "ideas.dlc.body_parts": "Body parts",
    "ideas.dlc.snapshot": "Model snapshot number",
    "ideas.column_names": "Column Names",
  },
  suite2p_data: {
    "ideas.Cell Extraction Method": "Cell Extraction Method",
    "ideas.Number of Frames": "Number of Frames",
    "ideas.Sampling Rate (Hz)": "Sampling Rate (Hz)",
    "ideas.Recording Duration (s)": "Recording Duration (s)",
    "ideas.Number of Planes": "Number of Planes",
    "ideas.Number of Channels": "Number of Channels",
    "ideas.Frame Width (px)": "Frame Width (px)",
    "ideas.Frame Height (px)": "Frame Height (px)",
    "ideas.Number of Extracted ROIs": "Number of Extracted ROIs",
    "ideas.Number of Accepted ROIs": "Number of Accepted ROIs",
    "ideas.Number of Rejected ROIs": "Number of Rejected ROIs",
    "ideas.Average ROI Radius (px)": "Average ROI Radius (px)",
    "ideas.Average trace amplitude": "Average trace amplitude",
    "ideas.Average event rate (Hz)": "Average event rate (Hz)",
    "ideas.Minimum event rate (Hz)": "Minimum event rate (Hz)",
    "ideas.Maximum event rate (Hz)": "Maximum event rate (Hz)",
  },
  bruker2p_movie: {
    "ideas.dataset.data_format_version": "Data Format Version",
    "ideas.dataset.date": "Date of Recording",
    "ideas.dataset.time": "Time of Recording",
    "ideas.dataset.num_channels": "Number of Channels",
    "ideas.dataset.num_planes": "Number of Planes",
    "ideas.dataset.system_id": "System ID",
    "ideas.dataset.system_description": "System Description",
    "ideas.spacingInfo.width": "Frame Width (px)",
    "ideas.spacingInfo.height": "Frame Height (px)",
    "ideas.spacingInfo.microns_per_pixel": "Microns per Pixel",
    "ideas.spacingInfo.objective_lens": "Objective Lens",
    "ideas.spacingInfo.optical_zoom": "Optical Zoom",
    "ideas.timingInfo.num_frames": "Number of Frames",
    "ideas.timingInfo.sampling_rate": "Sampling Rate (Hz)",
    "ideas.timingInfo.duration": "Recording Duration (s)",
  },
  dlc_model: {
    "ideas.dlc.task": "Model task name",
    "ideas.dlc.scorer": "Model scorer",
    "ideas.dlc.date": "Model training date",
    "ideas.dlc.multi_animal_project": "Multi-animal project",
    "ideas.dlc.model_neural_net_type": "Model neural network type",
    "ideas.dlc.body_parts": "Body parts",
    "ideas.dlc.snapshot": "Model snapshot number",
    "ideas.dlc.snapshot_median_train_error":
      "Model snapshot median train error",
    "ideas.dlc.snapshot_median_train_pcutoff_error":
      "Model snapshot median error with p-cutoff",
    "ideas.dlc.snapshot_median_test_error": "Model snapshot median test error",
    "ideas.dlc.snapshot_median_test_pcutoff_error":
      "Model snapshot median test error with p-cutoff",
  },
  dlc_evaluation_results: {
    "ideas.dlc.task": "Model task name",
    "ideas.dlc.scorer": "Model scorer",
    "ideas.dlc.date": "Model training date",
    "ideas.dlc.multi_animal_project": "Multi-animal project",
    "ideas.dlc.model_neural_net_type": "Model neural network type",
    "ideas.dlc.body_parts": "Body parts",
    "ideas.dlc.snapshot": "Model snapshot number",
    "ideas.dlc.snapshot_median_train_error":
      "Model snapshot median train error",
    "ideas.dlc.snapshot_median_train_pcutoff_error":
      "Model snapshot median error with p-cutoff",
    "ideas.dlc.snapshot_median_test_error": "Model snapshot median test error",
    "ideas.dlc.snapshot_median_test_pcutoff_error":
      "Model snapshot median test error with p-cutoff",
  },
  epoch_activity_data: {
    "ideas.cell_extraction_method": "Cell Extraction Method",
    "ideas.epoch_lengths": "Epoch Lengths (s)",
    "ideas.epoch_names": "Epoch Names",
    "ideas.epoch_colors": "Epoch Colors",
    "ideas.comparisons": "Comparisons",
    "ideas.correction_method": "Multiple Comparisons Correction",
    "ideas.sampling_rate": "Sampling Rate (Hz)",
    "ideas.num_cells": "Number of Cells",
    "ideas.scale_method": "Scaling method for calcium traces",
  },
  epoch_activity_comparison_data: {
    "ideas.num_epochs": "Number of Epochs",
    "ideas.epoch_names": "Epoch Names",
    "ideas.group_names": "Group Names",
    "ideas.num_files": "Number of files",
    "ideas.correction_method": "Multiple Comparisons Correction",
    "ideas.effect_size": "Effect Size",
    "ideas.alternative": "Alternative Hypothesis",
  },
  caiman_data: {
    "ideas.metrics.num_accepted_cells": "Number of accepted cells",
    "ideas.metrics.num_rejected_cells": "Number of rejected cells",
    "ideas.metrics.num_undecided_cells": "Number of undecided cells",
    "ideas.metrics.total_num_cells": "Total number of cells",
    "ideas.n_sessions": "Number of registered sessions",
    "ideas.cell_counts": "Number of cells per session",
    "ideas.n_regist_cells": "Number of registered cells across all sessions",
    "ideas.yield": "Registration yield (%)",
    "ideas.mean_overlap": "Average overlap of registered cells (Jaccard Index)",
    "ideas.mean_centroid_dist":
      "Average pairwise centroid distance of registered cells (px)",
    "ideas.mean_overlap_nonreg":
      "Average overlap of non-registered cells (Jaccard Index)",
    "ideas.mean_centroid_dist_nonreg":
      "Average pairwise centroid distance of non-registered cells (px)",
    "ideas.n_session_pairs": "Number of registered session pairs",
    "ideas.r_pre": "Pre-alignment Pearson's r",
    "ideas.r_post": "Post-alignment Pearson's r",
    "ideas.mean_shifts": "Average x/y shifts (px)",
    "ideas.max_shifts": "Maximum x/y shifts (px)",
  },
  motion_correction_qc_data: {
    "ideas.metrics.min_x_rigid_shift": "Minimum rigid x shift",
    "ideas.metrics.max_x_rigid_shift": "Maximum rigid x shift",
    "ideas.metrics.min_y_rigid_shift": "Minimum rigid y shift",
    "ideas.metrics.max_y_rigid_shift": "Maximum rigid y shift",
    "ideas.metrics.min_x_pw_rigid_shift": "Minimum piecewise rigid x shift",
    "ideas.metrics.max_x_pw_rigid_shift": "Maximum piecewise rigid x shift",
    "ideas.metrics.min_y_pw_rigid_shift": "Minimum piecewise rigid y shift",
    "ideas.metrics.max_y_pw_rigid_shift": "Maximum piecewise rigid y shift",
    "ideas.metrics.num_patches": "Number of patches",
  },
  // SHOW ALL METADATA
  modulation_data: undefined,
  correlation_data: undefined,
  movie_zones: undefined,
  zone_occupancy_analysis: undefined,
  locomotion_comparison_data: undefined,
  config: undefined,
  combined_correlation_data: undefined,
  suite2p_classifier: undefined,
  dlc_labeled_data: undefined,
  dlc_config: undefined,
  combined_zone_occupancy_analysis_data: undefined,
  unknown: undefined,
};

// @TODO ID-2041 remove once DLC movies have their own type
export const UNSELECTABLE_METADATA_KEYS = [
  "dlc.model_neural_net_type",
  "dlc.body_parts",
];
